import { useState, useEffect } from 'react';
import { Icons, IconBtn, AvatarChip } from './UI';
import { TaskForm } from './TaskForm';
import { TrackerForm } from './TrackerForm';
import { TrackerDetail } from './TrackerDetail';
import { ReportModal } from './Report';
import { STATUS_META, PRIORITY_COLOR, PRIORITY_DOT, STATUSES, PRIORITIES, TEAM, today, nowISO, fmtDate, linkify } from '../utils/constants';
import { api } from '../utils/api';


// ── Task Card ─────────────────────────────────────────────────────────────────
function TaskCard({ task, onClick, onEdit, onComplete }) {
  const sm = STATUS_META[task.status] || STATUS_META['Action Required'];
  const dot = PRIORITY_DOT[task.priority] || '#9ca3af';
  return (
    <div onClick={() => onClick(task)} style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', marginBottom: 8, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,.06)', borderLeft: `3px solid ${sm.color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: sm.bg, color: sm.color }}>{sm.icon} {task.status}</span>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block' }} title={task.priority} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', lineHeight: 1.4, marginBottom: 6 }}>{task.title}</div>
      {task.assignees?.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
          {task.assignees.map(a => <AvatarChip key={a} name={a} />)}
        </div>
      )}
      {task.nextSteps && (
        <div style={{ fontSize: 12, color: '#2563eb', background: '#eff6ff', padding: '5px 8px', borderRadius: 6, marginBottom: 6, lineHeight: 1.4 }}>
          → {task.nextSteps}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(task.dateAdded)}</span>
        <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          {task.status !== 'Complete' && <IconBtn onClick={() => onComplete(task.id)} title="Mark Complete"><Icons.Check /></IconBtn>}
          <IconBtn onClick={() => onEdit(task)} title="Edit"><Icons.Edit /></IconBtn>
        </div>
      </div>
    </div>
  );
}

// ── Add to Today Modal ───────────────────────────────────────────────────────
function AddToTodayModal({ task, currentUser, onConfirm, onClose }) {
  const [person, setPerson] = useState(currentUser || TEAM[0]);
  const [note, setNote] = useState('');
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:200 }} onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:'16px 16px 0 0', width:'100%', maxWidth:640, display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 16px 12px', borderBottom:'1px solid #f1f5f9' }}>
          <span style={{ fontSize:16, fontWeight:700 }}>Add to Today's List</span>
          <button onClick={onClose} style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:20, color:'#64748b' }}>×</button>
        </div>
        <div style={{ padding:16, display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ fontSize:14, color:'#475569', background:'#f8fafc', borderRadius:8, padding:'10px 12px', borderLeft:'3px solid #3b82f6' }}>
            {task.title}
          </div>
          <div>
            <div style={{ fontSize:12, fontWeight:600, color:'#475569', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:6 }}>Add to whose list?</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {TEAM.map(name => (
                <button key={name} onClick={() => setPerson(name)} style={{ background: person===name ? '#dbeafe' : '#f1f5f9', border: `1px solid ${person===name ? '#93c5fd' : '#e2e8f0'}`, color: person===name ? '#1d4ed8' : '#475569', borderRadius:20, padding:'5px 14px', fontSize:13, cursor:'pointer', fontWeight:500 }}>
                  {name}{name===currentUser ? ' (me)' : ''}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize:12, fontWeight:600, color:'#475569', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:6 }}>Focus note (optional)</div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Which part of this should they tackle today?" style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'10px 12px', fontSize:14, color:'#0f172a', outline:'none', width:'100%', fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }} />
          </div>
        </div>
        <div style={{ display:'flex', gap:8, padding:'12px 16px', borderTop:'1px solid #f1f5f9' }}>
          <button onClick={onClose} style={{ background:'#f1f5f9', border:'none', color:'#475569', borderRadius:8, padding:'10px 18px', cursor:'pointer', fontWeight:600, fontSize:14 }}>Cancel</button>
          <button onClick={() => onConfirm(person, note.trim())} style={{ background:'#3b82f6', border:'none', color:'#fff', borderRadius:8, padding:'10px 18px', cursor:'pointer', fontWeight:600, fontSize:14, flex:1 }}>
            ☑ Add to {person === currentUser ? 'My' : person + "'s"} Today
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tracker Card ─────────────────────────────────────────────────────────────
function TrackerCard({ tracker, onClick, onEdit }) {
  const sm = STATUS_META[tracker.status] || STATUS_META['Action Required'];
  return (
    <div onClick={() => onClick(tracker)} style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', marginBottom: 8, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,.06)', borderLeft: '3px solid #c2410c' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>📊 {tracker.subtype === 'tracker-task' ? 'Task Tracker' : 'Incident Tracker'}</span>
          <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: sm.bg, color: sm.color }}>{sm.icon} {tracker.status}</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          <IconBtn onClick={() => onEdit(tracker)} title="Edit"><Icons.Edit /></IconBtn>
        </div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', lineHeight: 1.4, marginBottom: 6 }}>{tracker.title}</div>
      {tracker.assignees?.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
          {tracker.assignees.map(a => <AvatarChip key={a} name={a} />)}
        </div>
      )}
      {tracker.notes && (
        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4, marginBottom: 4 }}>{tracker.notes.slice(0, 100)}{tracker.notes.length > 100 ? '...' : ''}</div>
      )}
      <div style={{ fontSize: 11, color: '#94a3b8' }}>Opened {fmtDate(tracker.dateAdded)}</div>
    </div>
  );
}

// ── Task Detail ───────────────────────────────────────────────────────────────
function TaskDetail({ task, onEdit, onComplete, onDelete, onBack, onAddToToday }) {
  const sm = STATUS_META[task.status] || STATUS_META['Action Required'];
  const [showAddToToday, setShowAddToToday] = useState(false);
  return (
    <div style={{ padding: 16, maxWidth: 680, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 4 }}><Icons.Back /> Back</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowAddToToday(true)} style={{ background: '#7c3aed', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>☑ Add to Today</button>
          <button onClick={() => onEdit(task)} style={{ background: '#3b82f6', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
        </div>
      </div>
      {showAddToToday && (
        <AddToTodayModal
          task={task}
          currentUser={onAddToToday?.currentUser}
          onConfirm={(person, note) => { onAddToToday?.fn(task, person, note); setShowAddToToday(false); }}
          onClose={() => setShowAddToToday(false)}
        />
      )}
      <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20, background: sm.bg, color: sm.color }}>{sm.icon} {task.status}</span>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '10px 0 8px', lineHeight: 1.3 }}>{task.title}</h2>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ color: PRIORITY_COLOR[task.priority], fontWeight: 600 }}>{task.priority} Priority</span>
        <span style={{ color: '#cbd5e1' }}>·</span>
        <span>Added {fmtDate(task.dateAdded)}</span>
        {task.dateResolved && <><span style={{ color: '#cbd5e1' }}>·</span><span>Resolved {fmtDate(task.dateResolved)}</span></>}
        {task.completedBy && <><span style={{ color: '#cbd5e1' }}>·</span><span>Closed by {task.completedBy}</span></>}
      </div>
      {task.assignees?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={sectionLabel}>Assigned To</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{task.assignees.map(a => <AvatarChip key={a} name={a} />)}</div>
        </div>
      )}
      {task.notes && (
        <div style={{ marginBottom: 16 }}>
          <div style={sectionLabel}>Notes / Description</div>
          <div style={bodyText} dangerouslySetInnerHTML={{ __html: linkify(task.notes) }} />
        </div>
      )}
      {task.nextSteps && (
        <div style={{ marginBottom: 16 }}>
          <div style={sectionLabel}>Next Steps</div>
          <div style={{ ...bodyText, borderLeft: '3px solid #3b82f6', paddingLeft: 12 }} dangerouslySetInnerHTML={{ __html: linkify(task.nextSteps) }} />
        </div>
      )}
      {task.completionNote && (
        <div style={{ marginBottom: 16 }}>
          <div style={sectionLabel}>Completion Note</div>
          <div style={{ ...bodyText, background: '#f0fdf4', borderLeft: '3px solid #22c55e', padding: '8px 12px', borderRadius: '0 6px 6px 0' }} dangerouslySetInnerHTML={{ __html: linkify(task.completionNote) }} />
        </div>
      )}
      {task.status !== 'Complete' && (
        <button onClick={() => onComplete(task.id)} style={{ background: '#22c55e', border: 'none', color: '#fff', borderRadius: 8, padding: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, width: '100%', marginTop: 8 }}>✅ Mark as Complete</button>
      )}
      <button onClick={() => onDelete(task.id)} style={{ background: 'transparent', border: '1px solid #fca5a5', color: '#ef4444', borderRadius: 8, padding: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, width: '100%', marginTop: 8 }}>🗑 Delete Task</button>
    </div>
  );
}

// ── Tasks Tab ─────────────────────────────────────────────────────────────────
export function TasksTab({ store, setStore, currentUser, defaultView, showReport, setShowReport, newActionRef }) {
  const [view, setView] = useState(defaultView || 'active');
  useEffect(() => { setView(defaultView || 'active'); }, [defaultView]);
  const [search, setSearch] = useState('');
  const [fStatus, setFStatus] = useState('All');
  const [fPriority, setFPriority] = useState('All');
  const [fAssignee, setFAssignee] = useState('All');
  const [sort, setSort] = useState('priority');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showTrackerForm, setShowTrackerForm] = useState(false);
  const [editingTracker, setEditingTracker] = useState(null);
  const [selectedTracker, setSelectedTracker] = useState(null);

  useEffect(() => { if (newActionRef) newActionRef.current = { task: () => setShowForm(true), tracker: () => setShowTrackerForm(true) }; }, [newActionRef]);

  const cycleSort = () => {
    const order = ['priority', 'newest', 'oldest'];
    setSort(s => order[(order.indexOf(s) + 1) % order.length]);
  };
  const sortLabel = sort === 'newest' ? '↓ Newest' : sort === 'oldest' ? '↑ Oldest' : '↕ Priority';

  const tasks = store.tasks || [];
  const active = tasks.filter(t => t.status !== 'Complete');
  const archived = tasks.filter(t => t.status === 'Complete');
  const pool = view === 'archived' ? archived : active;

  const filtered = pool.filter(t => {
    if (fStatus !== 'All' && t.status !== fStatus) return false;
    if (fPriority !== 'All' && t.priority !== fPriority) return false;
    if (fAssignee !== 'All' && !t.assignees?.includes(fAssignee)) return false;
    if (search && !t.title.toLowerCase().includes(search.toLowerCase()) && !(t.notes || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'newest') return (b.dateUpdated || b.dateAdded || '').localeCompare(a.dateUpdated || a.dateAdded || '');
    if (sort === 'oldest') return (a.dateUpdated || a.dateAdded || '').localeCompare(b.dateUpdated || b.dateAdded || '');
    const po = { High: 0, Medium: 1, Low: 2 };
    const so = { 'Action Required': 0, 'In Progress': 1, 'Pending Response': 2, 'Complete': 3 };
    const sa = so[a.status] ?? 3, sb = so[b.status] ?? 3;
    const pa = po[a.priority] ?? 2, pb = po[b.priority] ?? 2;
    return (sa - sb) || (pa - pb);
  });

  const saveTask = async (task) => {
    const isNew = !task.id;
    const saved = {
      ...task,
      id: isNew ? store.nextId : task.id,
      dateUpdated: nowISO(),
    };
    setStore(s => ({
      ...s,
      nextId: isNew ? s.nextId + 1 : s.nextId,
      tasks: isNew ? [...s.tasks, saved] : s.tasks.map(t => t.id === saved.id ? saved : t),
    }));
    setShowForm(false);
    setEditingTask(null);
    setSelectedTask(null);
    await api.saveTask(saved);
  };

  const completeTask = async (id) => {
    const task = tasks.find(t => t.id === id);
    if (!task) return;
    const updated = { ...task, status: 'Complete', dateResolved: task.dateResolved || today(), completedBy: currentUser || '', dateUpdated: nowISO() };
    setStore(s => ({ ...s, tasks: s.tasks.map(t => t.id === id ? updated : t) }));
    setSelectedTask(null);
    await api.saveTask(updated);
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    setStore(s => ({ ...s, tasks: s.tasks.filter(t => t.id !== id) }));
    setSelectedTask(null);
    await api.deleteTask(id);
  };

  const saveTracker = async (tracker) => {
    const isNew = !tracker.id;
    const saved = { ...tracker, id: isNew ? store.nextId : tracker.id, dateUpdated: nowISO() };
    setStore(s => ({
      ...s,
      nextId: isNew ? s.nextId + 1 : s.nextId,
      tasks: isNew ? [...s.tasks, saved] : s.tasks.map(t => t.id === saved.id ? saved : t),
    }));
    setShowTrackerForm(false);
    setEditingTracker(null);
    setSelectedTracker(null);
    await api.saveTask(saved);
  };

  const completeTracker = async (id) => {
    const tracker = tasks.find(t => t.id === id);
    if (!tracker) return;
    const updated = { ...tracker, status: 'Complete', dateResolved: tracker.dateResolved || today(), completedBy: currentUser || '', dateUpdated: nowISO() };
    setStore(s => ({ ...s, tasks: s.tasks.map(t => t.id === id ? updated : t) }));
    setSelectedTracker(null);
    await api.saveTask(updated);
  };

  if (selectedTracker) {
    return (
      <>
        <TrackerDetail
          tracker={selectedTracker}
          currentUser={currentUser}
          store={store}
          setStore={setStore}
          onBack={() => setSelectedTracker(null)}
          onEdit={t => { setEditingTracker(t); setSelectedTracker(null); }}
          onComplete={completeTracker}
          onDelete={async (id) => { if (!window.confirm('Delete this tracker?')) return; setStore(s => ({ ...s, tasks: s.tasks.filter(t => t.id !== id) })); setSelectedTracker(null); await api.deleteTask(id); }}
        />
        {editingTracker && <TrackerForm initial={editingTracker} currentUser={currentUser} onSave={saveTracker} onClose={() => setEditingTracker(null)} />}
      </>
    );
  }

  const addToToday = async (task, person, note) => {
    const newItem = {
      id: store.todayNextId || 1,
      person,
      date: new Date().toISOString().split('T')[0],
      taskId: task.id,
      customText: '',
      note: note || '',
      checked: false,
      sortOrder: store.todayNextId || 1,
    };
    setStore(s => ({ ...s, todayNextId: (s.todayNextId || 1) + 1, todayItems: [...(s.todayItems || []), newItem] }));
    await api.saveTodayItem(newItem);
    alert(`Added to ${person === currentUser ? 'your' : person + "'s"} Today list!`);
  };

  if (selectedTask) {
    return (
      <>
        <TaskDetail
          task={selectedTask}
          onBack={() => setSelectedTask(null)}
          onEdit={t => { setEditingTask(t); setSelectedTask(null); }}
          onComplete={completeTask}
          onDelete={deleteTask}
          onAddToToday={{ fn: addToToday, currentUser }}
        />
        {editingTask && <TaskForm initial={editingTask} currentUser={currentUser} onSave={saveTask} onClose={() => setEditingTask(null)} />}
      </>
    );
  }

  return (
    <>
      {/* Dark header extension for search */}
      <div style={{ background: '#0f172a', margin: '0 -12px', padding: '0 12px 2px' }}>
        <div style={{ display: 'flex', gap: 6, paddingBottom: 10 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, padding: '6px 10px', minWidth: 0 }}>
            <Icons.Search />
            <input placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} style={{ background: 'transparent', border: 'none', outline: 'none', color: '#f1f5f9', fontSize: 14, flex: 1, minWidth: 0 }} />
          </div>
          <button onClick={cycleSort} style={filterBtn(false)}>{sortLabel}</button>
          <button onClick={() => setShowFilters(f => !f)} style={filterBtn(showFilters)}><Icons.Filter /> Filters</button>
        </div>
        {showFilters && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingBottom: 10 }}>
            {[['fStatus', fStatus, setFStatus, ['All Statuses', ...STATUSES]], ['fPriority', fPriority, setFPriority, ['All Priorities', ...PRIORITIES]], ['fAssignee', fAssignee, setFAssignee, ['All People', ...TEAM]]].map(([key, val, setter, opts]) => (
              <select key={key} value={val} onChange={e => setter(e.target.value)} style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,.1)', color: '#e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 13, flex: 1, minWidth: 120 }}>
                {opts.map(o => <option key={o} value={o === opts[0] ? 'All' : o}>{o}</option>)}
              </select>
            ))}
          </div>
        )}
      </div>

      {/* Task list */}
      <div style={{ padding: '12px 0 80px' }}>
        {sorted.length === 0
          ? <div style={{ textAlign: 'center', color: '#94a3b8', padding: '48px 20px' }}>No tasks found. {view === 'active' && <span style={{ color: '#3b82f6', cursor: 'pointer' }} onClick={() => setShowForm(true)}>Add one →</span>}</div>
          : sorted.map(t => t.type === 'tracker'
            ? <TrackerCard key={t.id} tracker={t} onClick={setSelectedTracker} onEdit={t => setEditingTracker(t)} />
            : <TaskCard key={t.id} task={t} onClick={setSelectedTask} onEdit={t => { setEditingTask(t); }} onComplete={completeTask} />
          )
        }
      </div>

      {showForm && <TaskForm currentUser={currentUser} onSave={saveTask} onClose={() => setShowForm(false)} />}
      {editingTask && <TaskForm initial={editingTask} currentUser={currentUser} onSave={saveTask} onClose={() => setEditingTask(null)} />}
      {showTrackerForm && <TrackerForm currentUser={currentUser} onSave={saveTracker} onClose={() => setShowTrackerForm(false)} />}
      {editingTracker && <TrackerForm initial={editingTracker} currentUser={currentUser} onSave={saveTracker} onClose={() => setEditingTracker(null)} />}
      {showReport && <ReportModal store={store} onClose={() => setShowReport(false)} />}
    </>
  );
}


const sectionLabel = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 };
const bodyText = { fontSize: 14, color: '#334155', lineHeight: 1.6 };
const filterBtn = (on) => ({ background: on ? 'rgba(59,130,246,.2)' : 'rgba(255,255,255,.08)', border: `1px solid ${on ? '#3b82f6' : 'rgba(255,255,255,.1)'}`, color: on ? '#93c5fd' : '#94a3b8', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 });
